/**
 * Renders the Bootstrap offcanvas panel that lets users toggle field
 * visibility and reassign fields to different chart panel rows.
 *
 * The panel is populated lazily when the offcanvas opens, keyed on the
 * current table ID. Changes are persisted via userPlotConfig.ts and
 * reflected in the live chart via the onConfigChange callback.
 */

import { tableMetadata } from "./constants.js";
import { registryMetadata } from "./metadata.js";
import { getDefaultTableConfig } from "./plotConfig.js";
import {
	loadResolvedUserConfig,
	resetAllUserConfig,
	saveStationUserConfig,
	saveTableDefaultConfig,
	type TableUserConfig,
} from "./userPlotConfig.js";

export class FieldSelectionPanel {
	private tableId: number | undefined;
	private stationId: string | undefined;
	private readonly onConfigChange: (config: TableUserConfig) => void;

	private static toUserRowIndex(panelIndex: number, numPanels: number): number {
		return numPanels - 1 - panelIndex;
	}

	private static toPanelIndex(userRowIndex: number, numPanels: number): number {
		return numPanels - 1 - userRowIndex;
	}

	constructor(onConfigChange: (config: TableUserConfig) => void) {
		this.onConfigChange = onConfigChange;
	}

	/** Call whenever context changes (e.g. when offcanvas opens). */
	updateForContext(tableId: number, stationId: string): void {
		this.tableId = tableId;
		this.stationId = stationId;
		this.render();
	}

	private render(): void {
		const container = document.getElementById("fieldSelectionBody");
		if (
			!container ||
			this.tableId === undefined ||
			this.stationId === undefined
		)
			return;

		const tableId = this.tableId;
		const stationId = this.stationId;
		const stationName = stationMap[stationId]?.name ?? stationId;
		const fields = registryMetadata[tableId]?.fields ?? [];
		const tableInfo = tableMetadata[tableId];
		const stationContext = `Configuring ${stationName}, table format ${tableId}${tableInfo?.description ? `: ${tableInfo.description}` : ""}.`;
		const numPanels = tableInfo?.numDomains ?? 4;
		const defaultConfig = getDefaultTableConfig(tableId);
		const savedConfig = loadResolvedUserConfig(stationId, tableId);
		// Merge: defaults first, then saved overrides
		const currentConfig: TableUserConfig = { ...defaultConfig, ...savedConfig };

		// Build ordered groups from field registry
		const groupOrder: string[] = [];
		const groupMap = new Map<string, typeof fields>();
		for (const field of fields) {
			if (field.role === "timestamp") continue;
			if (!(field.visible ?? true)) continue; // skip registry-invisible fields
			if (!(field.name in defaultConfig)) continue; // only configurable fields
			const group = field.group?.trim() ?? "";
			if (!groupMap.has(group)) {
				groupMap.set(group, []);
				groupOrder.push(group);
			}
			const groupFields = groupMap.get(group);
			if (groupFields) {
				groupFields.push(field);
			}
		}

		const panelOptions = (selectedPanel: number): string => {
			const selectedUserRow = FieldSelectionPanel.toUserRowIndex(
				selectedPanel,
				numPanels,
			);
			return Array.from(
				{ length: numPanels },
				(_, i) =>
					`<option value="${i}" ${selectedUserRow === i ? "selected" : ""}>Row ${i + 1}</option>`,
			).join("");
		};

		let html = `
			<p class="text-muted small mb-3">
				Any changes are saved and persisted across page loads.
			</p>`;

		for (const groupName of groupOrder) {
			const groupFields = groupMap.get(groupName) ?? [];
			if (groupName) {
				html += `<h6 class="border-bottom pb-1 mt-3 mb-2 text-muted small text-uppercase fw-semibold">${groupName}</h6>`;
			}
			for (const field of groupFields) {
				const fc = currentConfig[field.name];
				const visible = fc?.visible ?? true;
				const panel = fc?.panel ?? 0;
				const label = field.description || field.label || field.name;
				const unit = field.unit
					? ` <span class="text-muted">(${field.unit})</span>`
					: "";

				html += `
					<div class="d-flex align-items-center mb-2 gap-2">
						<div class="form-check form-switch mb-0" style="min-width:40px">
							<input class="form-check-input field-visible-toggle"
								type="checkbox" role="switch"
								id="fvt-${field.name}"
								data-field="${field.name}"
								${visible ? "checked" : ""}>
						</div>
						<label class="flex-grow-1 small mb-0" for="fvt-${field.name}">${label}${unit}</label>
						<select class="form-select form-select-sm field-panel-select"
							data-field="${field.name}"
							style="width:82px"
							${visible ? "" : "disabled"}>
							${panelOptions(panel)}
						</select>
					</div>`;
			}
		}
		html += `<div class="border-top pt-3 mt-3">
			<div class="btn-group w-100 mb-3" role="group" aria-label="Configuration actions">
				<button class="btn btn-sm btn-outline-primary flex-grow-1" id="saveAsTableDefaultBtn"
					title="Apply this configuration to all stations with this table format."
					aria-label="Apply this configuration to all stations with this table format.">
					Apply to all stations<br> with same table format
				</button>
				<button class="btn btn-sm btn-outline-secondary" id="resetFieldConfigBtn">
				Reset to defaults
			</button>
			</div>
            <p class="text-muted small mb-3">
				${stationContext}
			</p>
		</div>`;

		container.innerHTML = html;

		// Wire events
		container
			.querySelectorAll<HTMLInputElement>(".field-visible-toggle")
			.forEach((el) => {
				el.addEventListener("change", () => {
					// Keep row selector in sync with the checkbox
					const select = container.querySelector<HTMLSelectElement>(
						`.field-panel-select[data-field="${el.dataset.field}"]`,
					);
					if (select) select.disabled = !el.checked;
					this.handleChange();
				});
			});

		container
			.querySelectorAll<HTMLSelectElement>(".field-panel-select")
			.forEach((el) => {
				el.addEventListener("change", () => this.handleChange());
			});

		document
			.getElementById("resetFieldConfigBtn")
			?.addEventListener("click", () => this.handleReset());

		document
			.getElementById("saveAsTableDefaultBtn")
			?.addEventListener("click", () => this.handleSaveAsTableDefault());
	}

	private buildCurrentConfig(): TableUserConfig {
		if (this.tableId === undefined) return {};
		const config: TableUserConfig = { ...getDefaultTableConfig(this.tableId) };
		const numPanels = tableMetadata[this.tableId]?.numDomains ?? 4;
		const container = document.getElementById("fieldSelectionBody");
		if (!container) return config;

		container
			.querySelectorAll<HTMLInputElement>(".field-visible-toggle")
			.forEach((el) => {
				const name = el.dataset.field;
				if (name && name in config) {
					config[name] = { ...config[name], visible: el.checked };
				}
			});

		container
			.querySelectorAll<HTMLSelectElement>(".field-panel-select")
			.forEach((el) => {
				const name = el.dataset.field;
				if (name && name in config) {
					const userRowIndex = Number.parseInt(el.value, 10);
					config[name] = {
						...config[name],
						panel: FieldSelectionPanel.toPanelIndex(userRowIndex, numPanels),
					};
				}
			});

		return config;
	}

	private handleChange(): void {
		if (this.tableId === undefined || this.stationId === undefined) return;
		const config = this.buildCurrentConfig();
		saveStationUserConfig(this.stationId, config);
		this.onConfigChange(config);
	}

	private handleReset(): void {
		if (this.tableId === undefined || this.stationId === undefined) return;
		resetAllUserConfig(this.stationId, this.tableId);
		this.render();
		this.onConfigChange(getDefaultTableConfig(this.tableId));
	}

	private handleSaveAsTableDefault(): void {
		if (this.tableId === undefined) return;
		const config = this.buildCurrentConfig();
		saveTableDefaultConfig(this.tableId, config);
	}
}
